import Close from "@mui/icons-material/Close";
import ImageOutlined from "@mui/icons-material/ImageOutlined";
import PictureAsPdfOutlined from "@mui/icons-material/PictureAsPdfOutlined";
import Videocam from "@mui/icons-material/Videocam";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import type { FC } from "react";
import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useToken } from "../../server/auth/use-token";
import { downloadFiles } from "../../utils/download-files";
import { useDialog } from "../../utils/hooks/use-dialog";
import type { PopoverController } from "../../utils/hooks/use-popover";
import { Progress, Success } from "../design-huddle/design-huddle-export-modal";
import { useGetDesignHuddleAccessToken } from "../design-huddle/use-design-huddle-access-token";
import { getDesignHuddleRenderUrls } from "../design-huddle/utils";
import { useAccountId } from "../generic/hooks/use-account-id";
import type { Asset } from "./server/digital-asset-service";

export const ExportMenu: FC<{
  menuController: PopoverController<HTMLElement>;
  asset: Asset;
}> = ({ menuController, asset }) => {
  const dialogController = useDialog();
  const getToken = useToken();
  const getAccessToken = useGetDesignHuddleAccessToken();
  const [fileFormat, setFileFormat] = useState("");

  const accountId = useAccountId();

  const [renderState, setRenderState] = useState<
    | {
        state: "loading";
        progress: number;
      }
    | {
        state: "hasError";
        error: string;
      }
    | {
        state: "hasData";
        urls: string[];
        progress: number;
      }
    | {
        state: "uninitialized";
        progress: number;
      }
  >({ state: "uninitialized", progress: 0 });

  const [cancelled, setCancelled] = useState(false);

  const handleExport = useCallback(
    (format: string) => {
      void (async () => {
        if (!accountId || !asset.project_id) return;
        if (renderState.state === "loading") return;
        setRenderState({ state: "loading", progress: 0 });
        setFileFormat(format);
        dialogController.handleOpen();

        const filename = `${asset.title}.${format}`;

        try {
          const downloadUrls = await getDesignHuddleRenderUrls({
            getToken,
            accessToken: await getAccessToken([asset.project_id]),
            projectId: asset.project_id,
            format,
            filename,
            onProgress: (progress) => {
              if (progress !== null) {
                setRenderState((prev) => ({ ...prev, progress }));
              }
            },
          });
          setRenderState({ state: "hasData", urls: downloadUrls, progress: 100 });
        } catch (e) {
          if (e instanceof TypeError) {
            setRenderState({ state: "hasError", error: e.message });
          }
        } finally {
          menuController.handleClose();
        }
      })();
    },
    [
      accountId,
      renderState.state,
      dialogController,
      asset.title,
      asset.project_id,
      getToken,
      getAccessToken,
      menuController,
    ],
  );

  useEffect(() => {
    if (cancelled) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setRenderState({ state: "uninitialized", progress: 0 });
      setCancelled(false);
      return;
    }

    if (renderState.state === "hasData") {
      void downloadFiles(renderState.urls, asset.title, fileFormat);
    }
  }, [asset.title, cancelled, fileFormat, renderState]);

  const handleCancel = useCallback(() => {
    setCancelled(true);
    dialogController.handleClose();
    menuController.handleClose();
  }, [dialogController, menuController]);

  if (renderState.state === "hasError") {
    toast.error(renderState.error);
  }

  const menuItems =
    asset.content_type === "Video"
      ? [
          <MenuItem
            key="mp4"
            onClick={() => handleExport("mp4")}
            data-analytics-id="asset-export-mp4"
            sx={{ display: "flex", gap: 1, minWidth: "8em" }}
          >
            <Videocam />
            MP4
          </MenuItem>,
        ]
      : [
          <MenuItem
            key="pdf"
            onClick={() => handleExport("pdf")}
            data-analytics-id="asset-export-pdf"
            sx={{ display: "flex", gap: 1, minWidth: "8em" }}
          >
            <PictureAsPdfOutlined />
            PDF
          </MenuItem>,
          <MenuItem
            key="png"
            onClick={() => handleExport("png")}
            data-analytics-id="asset-export-png"
            sx={{ display: "flex", gap: 1 }}
          >
            <ImageOutlined />
            PNG
          </MenuItem>,
        ];

  return (
    <>
      <Menu
        open={menuController.open}
        onClose={menuController.handleClose}
        anchorEl={menuController.anchorRef.current}
      >
        {menuItems}
      </Menu>
      <Dialog
        open={dialogController.open}
        onClose={renderState.state === "loading" ? undefined : dialogController.handleClose}
        fullWidth
      >
        <DialogTitle display="flex" component="div">
          <Typography flexGrow={1} variant="h5">
            {renderState.state === "hasData"
              ? "Export Successful"
              : renderState.state === "loading"
                ? "Rendering Design for Export"
                : "Export Error"}
          </Typography>
          {renderState.state !== "loading" && (
            <IconButton
              data-analytics-id="design-huddle-export-dialog-close"
              onClick={dialogController.handleClose}
            >
              <Close data-testid="CloseIcon" />
            </IconButton>
          )}
        </DialogTitle>

        {renderState.state === "hasData" ? (
          <Success
            urls={renderState.urls}
            title={asset.title}
            format={fileFormat}
            onClose={dialogController.handleClose}
          />
        ) : renderState.state === "loading" ? (
          <Progress onClose={handleCancel} progress={renderState.progress} />
        ) : (
          <DialogContent>
            <Typography variant="body1">
              An error occurred while exporting the design. Please refresh this page and try again.
            </Typography>
          </DialogContent>
        )}
      </Dialog>
    </>
  );
};
